// Customer portal — Account & Settings section.
//
// Four small subsections inside one .cust-card:
//   1. Your details      — read-only display of name / email / phone /
//                          address from the project payload. Edit is
//                          deliberately out-of-scope for V1 — the
//                          stub button toasts "coming soon".
//   2. Password          — single button that fires a stub email-reset
//                          flow. Backend endpoint pending —
//                          TODO: POST /api/customer/password-reset-request.
//   3. Notifications     — three checkboxes persisted to localStorage
//                          only (UI preferences, not business data,
//                          per [[feedback-localstorage-rule]]).
//   4. Portal branding   — informational card showing retailer logo +
//                          business name. Branding is applied globally
//                          by the chrome — this panel just makes it
//                          discoverable to the customer.
//
// Source-of-truth: NEW section (no equivalent in customer-live.js).
// Spec from Steve, 2026-05-19:
//   > "name, contact details, password change, notification preferences,
//   >  portal branding (retailer logo/colours already injected).
//   >  Keep it simple."
//
// PLATFORM-ONLY (per [[project-platform-messaging-only]]): no SMS,
// email, or WhatsApp UI controls anywhere.

import type {
  CustomerIdentity,
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";

import { formatAddress } from "../types";
import {
  canPromptInstall,
  detectPlatform,
  isInstalled,
  isIosSafari,
  isStandalone,
  onInstallAvailable,
  onInstalled,
  promptInstall,
} from "../shell/pwa";

import "./account.css";

// iOS Share glyph — custom inline SVG (1.5px stroke, 24×24), no icon libs.
const IOS_SHARE_GLYPH = `<svg class="acc-ios-share" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V4"/><path d="m8.5 7.5 3.5-3.5 3.5 3.5"/><path d="M6.5 11H6a1.5 1.5 0 0 0-1.5 1.5V19A1.5 1.5 0 0 0 6 20.5h12A1.5 1.5 0 0 0 19.5 19v-6.5A1.5 1.5 0 0 0 18 11h-.5"/></svg>`;

// "Add to home screen" guidance HTML for the current platform / install state.
// Front-end only — no push subscription here. Real Web Push is a later build.
function installBodyHtml(): string {
  if (isStandalone() || isInstalled()) {
    return `<p class="acc-install-done">You've added Kitchen Portal to your home screen — you're all set.</p>`;
  }
  const platform = detectPlatform();
  if (platform === "ios") {
    const safariNote = isIosSafari()
      ? ""
      : `<p class="acc-help">To add it, open this page in <strong>Safari</strong> first.</p>`;
    return `${safariNote}
      <ol class="acc-steps">
        <li>Tap the <strong>Share</strong> button ${IOS_SHARE_GLYPH} in Safari's toolbar.</li>
        <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
        <li>Tap <strong>Add</strong> — the portal appears on your home screen like an app.</li>
      </ol>`;
  }
  if (canPromptInstall()) {
    return `<button type="button" class="cust-btn cust-btn-primary acc-install-btn" data-role="pwa-install">Add to home screen</button>`;
  }
  // Android / desktop with no captured prompt — manual browser-menu route.
  return `<ol class="acc-steps">
      <li>Open your browser's menu.</li>
      <li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
    </ol>`;
}

// ── Notification preferences ────────────────────────────────────────────────

interface NotifPref {
  key: "project_updates" | "booking_reminders" | "promotional_offers";
  label: string;
  desc: string;
  defaultValue: boolean;
}

const NOTIF_PREFS: ReadonlyArray<NotifPref> = [
  {
    key: "project_updates",
    label: "Project updates",
    desc: "Quote progress, design milestones, install scheduling",
    defaultValue: true,
  },
  {
    key: "booking_reminders",
    label: "Booking reminders",
    desc: "Showroom and home-visit reminders ahead of appointments",
    defaultValue: true,
  },
  {
    key: "promotional_offers",
    label: "Promotional offers",
    desc: "Occasional offers from your retailer (off by default)",
    defaultValue: false,
  },
];

function storageKey(customerId: string, prefKey: NotifPref["key"]): string {
  return `tkos_customer_notif_${customerId}_${prefKey}`;
}

function readPref(customerId: string, pref: NotifPref): boolean {
  try {
    const raw = localStorage.getItem(storageKey(customerId, pref.key));
    if (raw == null) return pref.defaultValue;
    return raw === "1";
  } catch {
    return pref.defaultValue;
  }
}

function writePref(customerId: string, key: NotifPref["key"], value: boolean): void {
  try {
    localStorage.setItem(storageKey(customerId, key), value ? "1" : "0");
  } catch {
    // best-effort — Safari private mode etc.
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    };
    return map[c] ?? c;
  });
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0] ?? "")
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

const account: CustomerSection = {
  id: "account",
  label: "Account & settings",

  shouldRender(_: CustomerProject): boolean {
    // Always render — every customer can change their notification
    // preferences and view their own details.
    return true;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const { customer, retailer } = ctx.project;
    const fullName = `${customer.firstName} ${customer.lastName}`.trim() || "—";
    const email = (customer.email ?? "").trim() || "—";
    const phone = (customer.phone ?? "").trim() || "—";
    const address = formatAddress(customer.address);
    const postcode = (customer.postcode ?? "").trim() || "—";

    const card = document.createElement("section");
    card.className = "cust-card acc-card";
    card.setAttribute("aria-labelledby", "acc-title");

    // Build the four sub-panels inline. localStorage reads happen now
    // so the rendered checkboxes reflect the persisted state.
    const notifRowsHtml = NOTIF_PREFS.map((p) => {
      const checked = readPref(customer.id, p) ? "checked" : "";
      return `
        <label class="acc-notif-row" for="acc-notif-${esc(p.key)}">
          <input type="checkbox" id="acc-notif-${esc(p.key)}"
                 data-role="notif-toggle" data-key="${esc(p.key)}" ${checked}>
          <span class="acc-notif-body">
            <span class="acc-notif-label">${esc(p.label)}</span>
            <span class="acc-notif-desc">${esc(p.desc)}</span>
          </span>
        </label>
      `;
    }).join("");

    const logoMarkup = retailer.logoUrl
      ? `<img class="acc-brand-logo" src="${esc(retailer.logoUrl)}" alt="${esc(retailer.businessName)} logo">`
      : `<div class="acc-brand-initial" aria-hidden="true"
              style="background: var(--brand-primary)">${esc(initialsOf(retailer.businessName || "?"))}</div>`;

    card.innerHTML = `
      <h2 id="acc-title" class="cust-card-title">Account &amp; settings</h2>

      <section class="acc-section" aria-labelledby="acc-section-details">
        <h3 id="acc-section-details" class="acc-section-title">Your details</h3>
        <dl class="acc-dl">
          <div class="acc-dl-row"><dt>Name</dt><dd>${esc(fullName)}</dd></div>
          <div class="acc-dl-row"><dt>Email</dt><dd>${esc(email)}</dd></div>
          <div class="acc-dl-row"><dt>Phone</dt><dd>${esc(phone)}</dd></div>
          <div class="acc-dl-row"><dt>Address</dt><dd>${esc(address)}</dd></div>
          <div class="acc-dl-row"><dt>Postcode</dt><dd>${esc(postcode)}</dd></div>
        </dl>
        <button type="button" class="cust-btn cust-btn-secondary acc-edit-btn"
                data-role="edit-details">Edit details</button>
      </section>

      <section class="acc-section" aria-labelledby="acc-section-password">
        <h3 id="acc-section-password" class="acc-section-title">Password</h3>
        <p class="acc-help">Set or change the password you use to sign in to this portal.</p>
        <button type="button" class="cust-btn cust-btn-secondary acc-pw-btn"
                data-role="password-change">Change password</button>
      </section>

      <section class="acc-section" aria-labelledby="acc-section-notif">
        <h3 id="acc-section-notif" class="acc-section-title">Notification preferences</h3>
        <p class="acc-help">Choose which updates we send through this portal.</p>
        <div class="acc-notif-list">${notifRowsHtml}</div>
      </section>

      <section class="acc-section" aria-labelledby="acc-section-install" data-role="install-section">
        <h3 id="acc-section-install" class="acc-section-title">Add to home screen</h3>
        <!-- Must name whatever the home-screen icon will actually be labelled
             (manifest short_name / apple-mobile-web-app-title = "Kitchen
             Portal"), NOT the platform brand — otherwise this tells the
             homeowner to look for something they won't see. -->
        <p class="acc-help">Add Kitchen Portal to your home screen to open your project in one tap, like an app.</p>
        <div class="acc-install-body" data-role="install-body">${installBodyHtml()}</div>

        <div class="acc-push">
          <div class="acc-push-head">
            <span class="acc-push-title">Push notifications</span>
            <span class="acc-badge">Coming soon</span>
          </div>
          <p class="acc-help">Once switched on, we'll alert you about booking confirmations, new messages and payment reminders — even when the portal is closed.</p>
          <label class="acc-switch is-disabled" aria-disabled="true">
            <input type="checkbox" disabled aria-disabled="true">
            <span class="acc-switch-track"><span class="acc-switch-thumb"></span></span>
            <span class="acc-switch-label">Notify me on this device</span>
          </label>
          <p class="acc-note">Notifications aren't active yet — we're getting this ready, and you'll be able to turn them on right here.</p>
        </div>
      </section>

      <section class="acc-section" aria-labelledby="acc-section-brand">
        <h3 id="acc-section-brand" class="acc-section-title">Portal branding</h3>
        <div class="acc-brand">
          ${logoMarkup}
          <div class="acc-brand-text">
            <div class="acc-brand-name">${esc(retailer.businessName || "Your retailer")}</div>
            <div class="acc-brand-sub">This portal is provided by your retailer.</div>
          </div>
        </div>
      </section>

      <section class="acc-section" aria-labelledby="acc-section-delete">
        <h3 id="acc-section-delete" class="acc-section-title">Delete my account</h3>
        <p class="acc-help">Request permanent deletion of your account and personal data. Your retailer will action this within 30 days (GDPR right to erasure).</p>
        <button type="button" class="cust-btn cust-btn-secondary acc-delete-btn"
                data-role="delete-account">Request account deletion</button>
      </section>
    `;

    container.appendChild(card);

    // ── Wiring ────────────────────────────────────────────────────────
    const pwSection = card.querySelector<HTMLElement>('[aria-labelledby="acc-section-password"]');

    // Pull a human-readable error message out of an ApiError (the api client
    // throws with the raw JSON body as the message on non-2xx responses).
    function apiErrorMessage(err: unknown, fallback: string): string {
      if (err instanceof Error && err.message) {
        try {
          const j = JSON.parse(err.message) as { error?: unknown };
          if (j && typeof j.error === "string") return j.error;
        } catch { /* not JSON — use fallback */ }
      }
      return fallback;
    }

    // Password: real inline set/change flow (session-authed). A first-time set
    // (magic-link account, no password yet) skips the current-password field;
    // a change requires + verifies the current password server-side.
    function pwViewHtml(): string {
      return `
        <h3 id="acc-section-password" class="acc-section-title">Password</h3>
        <p class="acc-help">Set or change the password you use to sign in to this portal.</p>
        <button type="button" class="cust-btn cust-btn-secondary acc-pw-btn" data-role="password-change">Change password</button>
      `;
    }

    function renderPwView(): void {
      if (!pwSection) return;
      pwSection.innerHTML = pwViewHtml();
      pwSection
        .querySelector<HTMLButtonElement>('[data-role="password-change"]')
        ?.addEventListener("click", () => void showPwForm());
    }

    async function showPwForm(): Promise<void> {
      if (!pwSection) return;
      // Learn whether a password already exists so we know to ask for the
      // current one. Default to requiring it (safer) if the probe fails.
      let hasPassword = true;
      try {
        const r = await ctx.api<{ ok?: boolean; customer?: { hasPassword?: boolean } }>(
          "/customer/check",
          { method: "GET" },
        );
        hasPassword = r?.customer?.hasPassword !== false;
      } catch { /* keep the safe default */ }

      const currentFieldHtml = hasPassword
        ? `<label class="acc-fld">Current password
             <input type="password" name="currentPassword" autocomplete="current-password" placeholder="Your current password"></label>`
        : "";
      const saveLabel = hasPassword ? "Update password" : "Set password";

      pwSection.innerHTML = `
        <h3 id="acc-section-password" class="acc-section-title">Password</h3>
        <form class="acc-edit-form" data-role="pw-form" novalidate>
          ${currentFieldHtml}
          <label class="acc-fld">New password
            <input type="password" name="password" autocomplete="new-password" placeholder="At least 8 characters"></label>
          <label class="acc-fld">Confirm new password
            <input type="password" name="confirm" autocomplete="new-password" placeholder="Re-enter new password"></label>
          <div class="acc-pw-err cust-login-err" role="alert" aria-live="polite"></div>
          <div class="acc-edit-actions">
            <button type="submit" class="cust-btn cust-btn-primary" data-role="save-pw">${saveLabel}</button>
            <button type="button" class="cust-btn cust-btn-secondary" data-role="cancel-pw">Cancel</button>
          </div>
        </form>
      `;
      pwSection
        .querySelector<HTMLFormElement>('[data-role="pw-form"]')
        ?.addEventListener("submit", (e) => void onSavePw(e, hasPassword));
      pwSection
        .querySelector<HTMLButtonElement>('[data-role="cancel-pw"]')
        ?.addEventListener("click", renderPwView);
      pwSection.querySelector<HTMLInputElement>("input")?.focus();
    }

    async function onSavePw(e: Event, hasPassword: boolean): Promise<void> {
      e.preventDefault();
      const form = e.currentTarget as HTMLFormElement;
      const fd = new FormData(form);
      const currentPassword = String(fd.get("currentPassword") ?? "");
      const password = String(fd.get("password") ?? "");
      const confirm = String(fd.get("confirm") ?? "");
      const errEl = form.querySelector<HTMLElement>(".acc-pw-err");
      const setErr = (m: string): void => { if (errEl) errEl.textContent = m; };
      setErr("");
      if (hasPassword && !currentPassword) { setErr("Enter your current password."); return; }
      if (password.length < 8) { setErr("New password must be at least 8 characters."); return; }
      if (password !== confirm) { setErr("Those passwords don't match."); return; }

      const saveBtn = form.querySelector<HTMLButtonElement>('[data-role="save-pw"]');
      const saveLabel = hasPassword ? "Update password" : "Set password";
      if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = "Saving…"; }
      try {
        const body: Record<string, string> = { password };
        if (hasPassword) body.currentPassword = currentPassword;
        await ctx.api<{ ok?: boolean }>("/customer/set-password", {
          method: "POST",
          body: JSON.stringify(body),
        });
        renderPwView();
        ctx.showToast("Password updated", "success");
      } catch (err) {
        setErr(apiErrorMessage(err, "Couldn't update your password. Please try again."));
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = saveLabel; }
      }
    }

    // GDPR — request account deletion (confirmation screen → endpoint).
    const deleteBtn = card.querySelector<HTMLButtonElement>('[data-role="delete-account"]');
    function confirmDeletion(): Promise<boolean> {
      return new Promise((resolve) => {
        const d = document.createElement("dialog");
        d.className = "quote-dialog";
        d.innerHTML = `
          <form method="dialog" class="quote-dialog-form">
            <header class="quote-dialog-head"><h3>Request account deletion</h3></header>
            <div class="quote-dialog-body">
              <p>This asks ${esc(retailer.businessName || "your retailer")} to permanently delete your account and personal data.</p>
              <p>Once actioned, the following are removed: your name, contact details, address, messages, documents and saved quotes. Your retailer processes this within <strong>30 days</strong>.</p>
              <p>You can keep using your portal until then. Continue?</p>
            </div>
            <footer class="quote-dialog-footer">
              <button type="button" class="cust-btn cust-btn-secondary" data-role="d-cancel">Cancel</button>
              <button type="button" class="cust-btn quote-dialog-confirm" data-role="d-confirm">Request deletion</button>
            </footer>
          </form>`;
        card.appendChild(d);
        const done = (v: boolean): void => { try { d.close(); } catch { /* noop */ } d.remove(); resolve(v); };
        d.querySelector<HTMLButtonElement>('[data-role="d-cancel"]')?.addEventListener("click", () => done(false));
        d.querySelector<HTMLButtonElement>('[data-role="d-confirm"]')?.addEventListener("click", () => done(true));
        d.addEventListener("cancel", () => done(false));
        if (typeof d.showModal === "function") d.showModal(); else d.setAttribute("open", "");
      });
    }
    deleteBtn?.addEventListener("click", () => {
      void (async () => {
        if (!(await confirmDeletion())) return;
        deleteBtn.disabled = true;
        deleteBtn.textContent = "Sending request…";
        try {
          await ctx.api<unknown>("/customer/account/deletion-request", { method: "POST" });
          ctx.showToast("Deletion request sent — your retailer will action it within 30 days.", "success");
          deleteBtn.textContent = "Deletion requested";
        } catch {
          ctx.showToast("Could not send your request — please try again.", "error");
          deleteBtn.disabled = false;
          deleteBtn.textContent = "Request account deletion";
        }
      })();
    });
    const detailsSection = card.querySelector<HTMLElement>('[aria-labelledby="acc-section-details"]');

    // "Your details" is now editable: PATCH /api/customer/profile persists
    // name / phone / address to the account record. Email stays read-only (it
    // is the login identity). The section re-renders in place on save.
    function detailsViewHtml(): string {
      const fullName = `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim() || "—";
      const emailV = (customer.email ?? "").trim() || "—";
      const phoneV = (customer.phone ?? "").trim() || "—";
      const addressV = formatAddress(customer.address);
      return `
        <h3 id="acc-section-details" class="acc-section-title">Your details</h3>
        <dl class="acc-dl">
          <div class="acc-dl-row"><dt>Name</dt><dd>${esc(fullName)}</dd></div>
          <div class="acc-dl-row"><dt>Email</dt><dd>${esc(emailV)}</dd></div>
          <div class="acc-dl-row"><dt>Phone</dt><dd>${esc(phoneV)}</dd></div>
          <div class="acc-dl-row"><dt>Address</dt><dd>${esc(addressV)}</dd></div>
        </dl>
        <button type="button" class="cust-btn cust-btn-secondary acc-edit-btn" data-role="edit-details">Edit details</button>
      `;
    }

    function renderDetailsView(): void {
      if (!detailsSection) return;
      detailsSection.innerHTML = detailsViewHtml();
      detailsSection
        .querySelector<HTMLButtonElement>('[data-role="edit-details"]')
        ?.addEventListener("click", showEditForm);
    }

    function showEditForm(): void {
      if (!detailsSection) return;
      const addrStr = formatAddress(customer.address);
      detailsSection.innerHTML = `
        <h3 id="acc-section-details" class="acc-section-title">Your details</h3>
        <form class="acc-edit-form" data-role="details-form">
          <label class="acc-fld">First name
            <input type="text" name="firstName" value="${esc(customer.firstName ?? "")}" autocomplete="given-name"></label>
          <label class="acc-fld">Last name
            <input type="text" name="lastName" value="${esc(customer.lastName ?? "")}" autocomplete="family-name"></label>
          <label class="acc-fld">Phone
            <input type="tel" name="phone" value="${esc(customer.phone ?? "")}" autocomplete="tel"></label>
          <label class="acc-fld">Address
            <textarea name="address" rows="2" autocomplete="street-address">${esc(addrStr === "—" ? "" : addrStr)}</textarea></label>
          <label class="acc-fld">Postcode
            <input type="text" name="postcode" value="${esc(customer.postcode ?? "")}" autocomplete="postal-code"></label>
          <div class="acc-edit-actions">
            <button type="submit" class="cust-btn cust-btn-primary" data-role="save-details">Save changes</button>
            <button type="button" class="cust-btn cust-btn-secondary" data-role="cancel-details">Cancel</button>
          </div>
        </form>
      `;
      detailsSection
        .querySelector<HTMLFormElement>('[data-role="details-form"]')
        ?.addEventListener("submit", onSaveDetails);
      detailsSection
        .querySelector<HTMLButtonElement>('[data-role="cancel-details"]')
        ?.addEventListener("click", renderDetailsView);
    }

    async function onSaveDetails(e: Event): Promise<void> {
      e.preventDefault();
      const form = e.currentTarget as HTMLFormElement;
      const fd = new FormData(form);
      const payload = {
        firstName: String(fd.get("firstName") ?? "").trim(),
        lastName: String(fd.get("lastName") ?? "").trim(),
        phone: String(fd.get("phone") ?? "").trim(),
        address: String(fd.get("address") ?? "").trim(),
        postcode: String(fd.get("postcode") ?? "").trim(),
      };
      const saveBtn = form.querySelector<HTMLButtonElement>('[data-role="save-details"]');
      if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = "Saving…"; }
      try {
        const res = await ctx.api<{ ok?: boolean; customer?: Partial<CustomerIdentity> }>(
          "/customer/profile",
          { method: "PATCH", body: JSON.stringify(payload) },
        );
        const u = res?.customer;
        customer.firstName = u?.firstName ?? payload.firstName;
        customer.lastName = u?.lastName ?? payload.lastName;
        customer.phone = u?.phone ?? payload.phone;
        customer.address = u?.address ?? payload.address;
        customer.postcode = u?.postcode ?? payload.postcode;
        renderDetailsView();
        ctx.showToast("Details saved", "success");
      } catch {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = "Save changes"; }
        ctx.showToast("Couldn't save your details. Please try again.", "error");
      }
    }

    // Bind the (initial server-rendered) details view's Edit button.
    renderDetailsView();

    // Bind the (initial server-rendered) password view's Change button.
    renderPwView();

    // Notification toggles persist to localStorage immediately.
    const onNotifChange = (e: Event): void => {
      const target = e.target as HTMLInputElement | null;
      if (!target || target.dataset["role"] !== "notif-toggle") return;
      const key = target.dataset["key"] as NotifPref["key"] | undefined;
      if (!key) return;
      writePref(customer.id, key, target.checked);
      const pref = NOTIF_PREFS.find((p) => p.key === key);
      ctx.showToast(
        target.checked
          ? `${pref?.label ?? "Preference"} turned on`
          : `${pref?.label ?? "Preference"} turned off`,
        "info",
      );
    };
    card.addEventListener("change", onNotifChange);

    // ── Add to home screen ─────────────────────────────────────────────
    // Re-render the guidance body whenever install state changes (the native
    // prompt becomes available, or the app gets installed), and wire the
    // Android/desktop install button to the captured prompt.
    const installBody = card.querySelector<HTMLElement>('[data-role="install-body"]');
    function renderInstallBody(): void {
      if (!installBody) return;
      installBody.innerHTML = installBodyHtml();
      installBody
        .querySelector<HTMLButtonElement>('[data-role="pwa-install"]')
        ?.addEventListener("click", () => {
          void (async () => {
            const outcome = await promptInstall();
            if (outcome === "accepted") {
              ctx.showToast("Added to your home screen", "success");
            }
            renderInstallBody();
          })();
        });
    }
    renderInstallBody();
    const offInstallAvailable = onInstallAvailable(renderInstallBody);
    const offInstalled = onInstalled(renderInstallBody);

    return () => {
      // Details + password edit/save/cancel listeners live on elements inside
      // `card`, so they're torn down when the card is removed below.
      card.removeEventListener("change", onNotifChange);
      offInstallAvailable();
      offInstalled();
      if (card.parentNode === container) {
        container.removeChild(card);
      }
    };
  },
};

export default account;
